/**
 * Tier 1 review screen (tiered-test-authoring task 3.4, plus 3.8's diff
 * viewer and 3.12's install instructions).
 *
 * The slideshow the spec asks for: every recording a client submitted via the
 * extension, shown as images and plain-language step descriptions — never a
 * selector, never the word "assertion". Approving/saving here is a QA/ADMIN
 * decision (this whole screen sits behind the console's normal sign-in); the
 * CLIENT who made the recording never sees this page.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type Tier1RecordingDetail, type Tier1RecordingSummary, type Tier1Finding, type Tier1SnapshotPair, type Tier1Category } from '../api';
import { diffImages, type ImageDiffResult } from '../lib/imageDiff';

const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;

export function Tier1Screen() {
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState<Tier1RecordingSummary[]>([]);
  const [categories, setCategories] = useState<Tier1Category[]>([]);
  const [findings, setFindings] = useState<Tier1Finding[]>([]);
  const [pairs, setPairs] = useState<Tier1SnapshotPair[]>([]);
  const [selected, setSelected] = useState<Tier1RecordingDetail | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [showExport, setShowExport] = useState(false);

  // Filters (task: "filters area so we can filter tests by anything priority
  // category name search or whatever") — kept as plain UI state, never sent
  // to the server: the recording list is already small enough (a client's
  // extension submissions, not a full regression suite) that filtering the
  // list this screen already fetched is simpler than a second query shape.
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [groupByCategory, setGroupByCategory] = useState(true);

  const refresh = () => {
    api.tier1Recordings().then((r) => setRecordings(r.recordings)).catch((e) => setError(String(e)));
    api.tier1Categories().then((r) => setCategories(r.categories)).catch(() => {});
    api.tier1Findings().then((r) => setFindings(r.findings)).catch(() => {});
    api.tier1SnapshotPairs().then((r) => setPairs(r.pairs)).catch(() => {});
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const search = filterSearch.trim().toLowerCase();
    return recordings.filter((r) => {
      if (filterCategory !== 'all' && (r.category || 'Uncategorized') !== filterCategory) return false;
      if (filterPriority !== 'all' && (r.priority || '') !== filterPriority) return false;
      if (search && !(r.name || `${r.app} recording`).toLowerCase().includes(search)) return false;
      return true;
    });
  }, [recordings, filterCategory, filterPriority, filterSearch]);

  const groups = useMemo(() => {
    if (!groupByCategory) return [{ category: null as string | null, recordings: filtered }];
    const byCategory = new Map<string, Tier1RecordingSummary[]>();
    for (const r of filtered) {
      const key = r.category || 'Uncategorized';
      (byCategory.get(key) ?? byCategory.set(key, []).get(key)!).push(r);
    }
    return [...byCategory.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, recs]) => ({ category, recordings: recs }));
  }, [filtered, groupByCategory]);

  const categoryOptions = useMemo(() => {
    const names = new Set(categories.map((c) => c.name));
    for (const r of recordings) if (r.category) names.add(r.category);
    return [...names].sort();
  }, [categories, recordings]);

  const open = async (id: string) => {
    try {
      const { recording } = await api.tier1Recording(id);
      setSelected(recording);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const review = async (id: string, save: boolean) => {
    await api.reviewTier1Recording(id, save);
    refresh();
    setSelected(null);
  };

  /**
   * The one real Tier 1 → Tier 2 path: build the recipe YAML from the
   * recording (server-side, no file written yet), ask the QA person what to
   * call the recipe FILE, write it via the exact same `createRecipe` route
   * `RecipesScreen.tsx`'s own "New recipe" modal uses, then land on that
   * recipe's page — mirroring `RecipesScreen.tsx`'s own `onCreated` pattern
   * instead of leaving the client's recording stuck as a permanent Tier 1
   * entry (which is all "Save as a runnable test" ever did).
   */
  const promoteToRecipe = async (recording: Tier1RecordingDetail) => {
    const suggestedId = (recording.name || `${recording.app}-recording`)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'promoted-recording';
    const recipeId = window.prompt('Name this recipe (used as its file name in Recipe Builder):', suggestedId);
    if (!recipeId) return;

    setPromoting(true);
    try {
      const { recipeYaml } = await api.promoteTier1ToRecipe(recording.id, []);
      await api.createRecipe(recipeId, recipeYaml);
      navigate(`/recipes?open=${encodeURIComponent(recipeId)}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPromoting(false);
    }
  };

  const remove = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.deleteTier1Recording(id);
      refresh();
      setSelected(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Client recordings</h1>
        <div className="flex gap-2">
          <button className="btn" onClick={() => setShowExport(true)} data-testid="tier1-open-export">
            Export to Excel
          </button>
          <button className="btn" onClick={() => setShowInstructions((v) => !v)} data-testid="tier1-toggle-instructions">
            {showInstructions ? 'Hide' : 'How do I install this?'}
          </button>
          <a className="btn btn-primary" href="/api/tier1/extension" data-testid="tier1-download-extension">
            Download the recording extension
          </a>
        </div>
      </div>

      {showInstructions && <InstallInstructions />}
      {showExport && <Tier1ExportModal onClose={() => setShowExport(false)} />}

      <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
        Submitted by clients through the browser extension — no console session
        required on their end.
      </p>

      {error && <div className="surface px-4 py-3 text-sm" style={{ color: 'hsl(var(--fail))' }}>{error}</div>}

      {selected ? (
        <RecordingDetail
          recording={selected}
          categories={categoryOptions}
          onClose={() => setSelected(null)}
          onReview={review}
          onDelete={remove}
          onPromote={promoteToRecipe}
          onMetaSaved={(updated) => { setSelected(updated); refresh(); }}
          promoting={promoting}
        />
      ) : (
        <>
          <div className="surface flex flex-wrap items-end gap-3 p-4">
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Search by name</span>
              <input
                className="field"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="e.g. checkout"
                data-testid="tier1-filter-search"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Category</span>
              <select className="field" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} data-testid="tier1-filter-category">
                <option value="all">All categories</option>
                {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="Uncategorized">Uncategorized</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="label">Priority</span>
              <select className="field" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} data-testid="tier1-filter-priority">
                <option value="all">All priorities</option>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="ml-auto flex items-center gap-2 text-sm">
              <input type="checkbox" checked={groupByCategory} onChange={(e) => setGroupByCategory(e.target.checked)} data-testid="tier1-group-toggle" />
              Group by category
            </label>
          </div>

          {groups.map((group) => (
            <div key={group.category ?? 'all'} className="surface flex flex-col gap-0">
              {group.category && (
                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--text-faint))', borderBottom: '1px solid hsl(var(--border))' }}>
                  {group.category} ({group.recordings.length})
                </div>
              )}
              {group.recordings.length === 0 && (
                <p className="p-4 text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
                  {recordings.length === 0 ? 'No recordings yet — nothing has been submitted from the extension.' : 'Nothing matches these filters.'}
                </p>
              )}
              {group.recordings.map((r) => (
                <div
                  key={r.id}
                  className="flex items-stretch"
                  style={{ borderBottom: '1px solid hsl(var(--border))' }}
                >
                  <button
                    className="flex flex-1 items-center gap-3 px-4 py-3 text-left text-sm"
                    onClick={() => open(r.id)}
                    data-testid={`tier1-recording-${r.id}`}
                  >
                    <span className="flex-1">
                      <span className="font-medium">{r.name || `${r.app} recording`}</span>
                      {' — '}{r.app} · {r.environment} · {r.stepCount} step{r.stepCount === 1 ? '' : 's'}
                      {r.priority && <span> · {r.priority}</span>}
                      {r.findingCount > 0 && <span style={{ color: 'hsl(var(--warn))' }}> · {r.findingCount} marked broken</span>}
                    </span>
                    <span className={`pill ${r.reviewed ? 'pill-pass' : 'pill-warn'}`}>{r.reviewed ? 'reviewed' : 'needs review'}</span>
                  </button>
                  {/* Previously there was no way to remove a client's submission at
                      all — once saved as a runnable test it stayed forever. */}
                  <button
                    onClick={() => remove(r.id, `${r.app} recording`)}
                    data-testid={`tier1-delete-${r.id}`}
                    title="Delete this recording"
                    aria-label="Delete this recording"
                    className="px-3 text-sm opacity-40 transition hover:opacity-100"
                    style={{ color: 'hsl(var(--fail))' }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ))}

          {findings.length > 0 && (
            <div className="surface p-4">
              <h2 className="mb-2 text-sm font-semibold">Findings from "this is broken" (task 3.6)</h2>
              <ul className="flex flex-col gap-1 text-sm">
                {findings.map((f) => (
                  <li key={f.id}>Recording {f.recordingId}, step {f.stepIndex + 1}: {f.note}</li>
                ))}
              </ul>
            </div>
          )}

          <SnapshotDiffs pairs={pairs} />
        </>
      )}
    </div>
  );
}

function InstallInstructions() {
  return (
    <div className="surface p-4 text-sm" data-testid="tier1-instructions">
      <h2 className="mb-2 font-semibold">Installing the recording extension</h2>
      <ol className="ml-4 flex flex-col gap-1.5" style={{ listStyle: 'decimal' }}>
        <li>Click "Download the recording extension" above — it saves a `.zip` file. This is never published on the Chrome Web Store; downloading it here is the only way to get it.</li>
        <li>Unzip it somewhere you'll remember.</li>
        <li>Open <code>chrome://extensions</code> in Chrome.</li>
        <li>Turn on "Developer mode" (top right of that page).</li>
        <li>Click "Load unpacked" and select the unzipped folder.</li>
        <li>Pin the extension (click the puzzle-piece icon in the toolbar, then the pin next to "Teaching Machines QA Recorder") so it's always one click away.</li>
        <li>Open the app you want to record (a development or QA link — production is never offered), click the extension's icon, and press Start.</li>
      </ol>
      <p className="mt-3 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
        Chrome will show a warning that this is an extension not from the Web Store — that's expected for an internal tool distributed this way.
      </p>
    </div>
  );
}

function SnapshotDiffs({ pairs }: { pairs: Tier1SnapshotPair[] }) {
  if (pairs.length === 0) return null;
  return (
    <div className="surface flex flex-col gap-4 p-4">
      <h2 className="text-sm font-semibold">Before/after snapshots (task 3.8)</h2>
      {pairs.map((pair) => (
        <SnapshotDiffRow key={`${pair.app}::${pair.path}`} pair={pair} />
      ))}
    </div>
  );
}

function SnapshotDiffRow({ pair }: { pair: Tier1SnapshotPair }) {
  const [diff, setDiff] = useState<ImageDiffResult | 'loading' | 'error' | null>(null);

  useEffect(() => {
    if (!pair.after) return;
    setDiff('loading');
    diffImages(pair.before.screenshot, pair.after.screenshot)
      .then(setDiff)
      .catch(() => setDiff('error'));
  }, [pair.before.id, pair.after?.id]);

  return (
    <div className="flex flex-col gap-2 border-t pt-3" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`tier1-snapshot-${pair.app}`}>
      <div className="flex items-center gap-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
        <span className="font-medium" style={{ color: 'hsl(var(--text-muted))' }}>{pair.app} · {pair.path}</span>
        {pair.after ? (
          diff === 'loading' ? (
            <span>comparing…</span>
          ) : diff === 'error' ? (
            <span style={{ color: 'hsl(var(--fail))' }}>could not compare images</span>
          ) : diff ? (
            <span className={`pill ${diff.changed ? 'pill-warn' : 'pill-pass'}`}>
              {diff.changed ? `changed — ~${diff.diffPercent.toFixed(1)}% of the page` : 'unchanged'}
            </span>
          ) : null
        ) : (
          <span>only one snapshot so far — take another after your deploy to see what changed</span>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto">
        <Thumbnail src={pair.before.screenshot} alt={`${pair.app} ${pair.path} — before`} />
        {pair.after && <Thumbnail src={pair.after.screenshot} alt={`${pair.app} ${pair.path} — after`} />}
      </div>
    </div>
  );
}

/**
 * A screenshot shown small enough to scan a list of them, but never cropped
 * or stretched — `object-contain` inside a FIXED box means a wide desktop
 * capture shrinks to fit without losing its real proportions, unlike
 * `object-cover`, which crops most of a wide capture away to fill a square-
 * ish box. Click to see it full size, since "small but readable" cannot both
 * be true of the same fixed thumbnail for a full-page screenshot's worth of
 * text.
 */
function Thumbnail({ src, alt }: { src: string; alt: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="shrink-0 overflow-hidden rounded border p-0 transition hover:opacity-80"
        style={{ borderColor: 'hsl(var(--border))', width: '14rem', height: '9rem', background: 'hsl(var(--bg-overlay))' }}
        title="Click to enlarge"
      >
        <img src={src} alt={alt} className="h-full w-full object-contain" />
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: 'hsl(var(--bg) / 0.92)' }}
          onClick={() => setOpen(false)}
        >
          <img src={src} alt={alt} className="max-h-[90vh] max-w-full object-contain" />
        </div>
      )}
    </>
  );
}

function RecordingDetail({
  recording,
  categories,
  onClose,
  onReview,
  onDelete,
  onPromote,
  onMetaSaved,
  promoting,
}: {
  recording: Tier1RecordingDetail;
  categories: string[];
  onClose: () => void;
  onReview: (id: string, save: boolean) => void;
  onDelete: (id: string, name: string) => void;
  onPromote: (recording: Tier1RecordingDetail) => void;
  onMetaSaved: (recording: Tier1RecordingDetail) => void;
  promoting: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <button className="btn btn-ghost self-start text-xs" onClick={onClose}>← Back</button>
      <h2 className="text-sm font-semibold">{recording.name || `${recording.app} recording`}</h2>

      <RecordingMetaForm recording={recording} categories={categories} onSaved={onMetaSaved} />

      <ol className="flex flex-col gap-3">
        {recording.steps.map((step) => (
          <li key={step.index} className="surface flex flex-col gap-2 p-4" data-testid={`tier1-step-${step.index}`}>
            <span className="text-sm font-medium">{step.index + 1}. {step.description}</span>
            {step.screenshot ? (
              <Thumbnail src={step.screenshot} alt={step.description} />
            ) : (
              <p className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>No screenshot for this step.</p>
            )}
            {step.brokenNote && (
              <p className="text-xs" style={{ color: 'hsl(var(--fail))' }}>⚠ Marked broken: {step.brokenNote}</p>
            )}
          </li>
        ))}
      </ol>
      <div className="flex gap-2">
        <button
          className="btn btn-primary"
          onClick={() => onPromote(recording)}
          disabled={promoting}
          data-testid="tier1-promote-to-recipe"
        >
          {promoting ? 'Creating recipe…' : 'Promote to recipe (Tier 2)'}
        </button>
        <button className="btn" onClick={() => onReview(recording.id, true)} data-testid="tier1-save">
          Save as a runnable test
        </button>
        <button className="btn" onClick={() => onReview(recording.id, false)} data-testid="tier1-mark-reviewed">
          Mark reviewed without saving
        </button>
        <button
          className="btn ml-auto"
          onClick={() => onDelete(recording.id, `${recording.app} recording`)}
          data-testid="tier1-delete"
          style={{ color: 'hsl(var(--fail))' }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/**
 * Category/priority/comments and the precondition the extension captured
 * (or the reviewer's correction of it) — the fields the ticket asks for
 * beyond the name-only prompt the extension used to offer. Saved explicitly
 * (not on every keystroke) so a reviewer editing several fields doesn't fire
 * a PATCH per character.
 */
function RecordingMetaForm({
  recording,
  categories,
  onSaved,
}: {
  recording: Tier1RecordingDetail;
  categories: string[];
  onSaved: (recording: Tier1RecordingDetail) => void;
}) {
  const [category, setCategory] = useState(recording.category ?? '');
  const [priority, setPriority] = useState(recording.priority ?? '');
  const [comments, setComments] = useState(recording.comments ?? '');
  const [requiresLogin, setRequiresLogin] = useState(recording.precondition?.requiresLogin ?? false);
  const [startPath, setStartPath] = useState(recording.precondition?.startPath ?? '');
  const [note, setNote] = useState(recording.precondition?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    category !== (recording.category ?? '') ||
    priority !== (recording.priority ?? '') ||
    comments !== (recording.comments ?? '') ||
    requiresLogin !== (recording.precondition?.requiresLogin ?? false) ||
    startPath !== (recording.precondition?.startPath ?? '') ||
    note !== (recording.precondition?.note ?? '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const { recording: updated } = await api.updateTier1Meta(recording.id, {
        category: category || null,
        priority: (priority || null) as Tier1RecordingDetail['priority'],
        comments: comments || null,
        precondition: startPath ? { requiresLogin, startPath, note: note || undefined } : null,
      });
      onSaved(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface flex flex-col gap-3 p-4" data-testid="tier1-meta-form">
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="label">Category</span>
          <input
            className="field"
            list="tier1-category-options"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Login page"
            data-testid="tier1-meta-category"
          />
          <datalist id="tier1-category-options">
            {categories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="label">Priority</span>
          <select className="field" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="tier1-meta-priority">
            <option value="">Not set</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="label">Comments</span>
        <textarea
          className="field"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          rows={2}
          data-testid="tier1-meta-comments"
        />
      </label>

      <div className="flex flex-col gap-2 border-t pt-3" style={{ borderColor: 'hsl(var(--border))' }}>
        <span className="label">Precondition (what replay must set up before the recorded steps)</span>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={requiresLogin} onChange={(e) => setRequiresLogin(e.target.checked)} data-testid="tier1-meta-requires-login" />
          The client was already signed in when this recording started — replay must sign in with a QA automation account first
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="label">Page the recording actually started from</span>
          <input
            className="field"
            value={startPath}
            onChange={(e) => setStartPath(e.target.value)}
            placeholder="/courses"
            data-testid="tier1-meta-start-path"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="label">Note (e.g. a modal that was already open — not reproduced automatically)</span>
          <input
            className="field"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="tier1-meta-note"
          />
        </label>
      </div>

      {error && <p className="text-xs" style={{ color: 'hsl(var(--fail))' }}>{error}</p>}
      <button className="btn self-start" onClick={save} disabled={!dirty || saving} data-testid="tier1-meta-save">
        {saving ? 'Saving…' : 'Save details'}
      </button>
    </div>
  );
}

function Tier1ExportModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto p-6"
      style={{ background: 'hsl(var(--bg) / 0.9)' }}
      data-testid="tier1-export-modal"
    >
      <div className="surface my-8 w-full max-w-md p-0">
        <div className="flex items-baseline gap-3 p-4" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 className="text-base font-semibold">Export Tier 1 tests</h2>
          <button className="btn btn-ghost ml-auto text-xs" onClick={onClose} data-testid="tier1-export-modal-close">
            Close
          </button>
        </div>
        <div className="flex flex-col gap-4 p-5">
          <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
            One workbook, every recorded test, split into a separate sheet per category —
            id, name, category, preconditions, recorded steps, assertions, last pass/fail,
            expected/actual result, priority and comments.
          </p>
          <a className="btn btn-primary" href={api.tier1ExportUrl()} data-testid="tier1-export-download">
            Download .xlsx
          </a>
        </div>
      </div>
    </div>
  );
}
